import React from 'react';
import { validateEmail } from '@tesla/validation';
import { arrayOf, bool, shape, string } from 'prop-types';
import { useIntl } from 'react-intl';
import { FormFieldset } from '@tesla/design-system-react';
import { FormField } from 'informed';
import XRegExp from 'xregexp';
import messages from './messages';
import { useTCLFormApi } from '../../../hooks';
import formStyle from '../../JobApplicationForm/style.module.scss';
import style from './style.module.scss';
import { isInternalEmail } from '../../../utils/common';

const PersonalSection = ({ fields, optionalResumeValidation }) => {
  const intl = useIntl();

  const { formatMessage } = intl;
  const formHelper = useTCLFormApi();

  const validateAlphaCharacters = (value) => {
    const pattern = XRegExp("^[\\p{L} .’'-]+$", 'gu');
    return pattern.test(value);
  };

  const validateEmailAddress = (value) => {
    if (value) {
      if (validateEmail(value) === true) {
        if (isInternalEmail(value) === true) {
          return (
            formHelper?.schema?.formSchema?.properties?.email?.errorMessage?.internalEmail ??
            'invalid'
          );
        }
      } else {
        return (
          formHelper?.schema?.formSchema?.properties?.email?.errorMessage?.invalidEmail ?? 'invalid'
        );
      }
    }
    return undefined;
  };

  const validateOptionalResume = (value, values) => {
    const {
      personal: { resume, evidenceOfExcellence, profileLinks },
    } = values;
    const errorMessage = formatMessage(messages.OptionalResumeValidationMessage);
    const hasProfileLink = () =>
      profileLinks && profileLinks.filter((item) => item.type && item.link).length > 0;

    return !resume && !evidenceOfExcellence && !hasProfileLink() ? errorMessage : undefined;
  };

  /**
   * Ensures phone number is correctly validated as required.
   *
   * @param value
   *   The phone value object.
   * @returns {boolean|string}
   */
  const validateRequiredPhone = (value) =>
    value.number == null ? formatMessage(messages.PhoneInputValidationMessage) : undefined;

  return (
    <>
      <div className={`${formStyle.FormSection} tds-layout tds-layout-2col`}>
        {fields.firstName && (
          <FormField
            {...formHelper.field(fields.firstName)}
            validate={(value) =>
              !value || !validateAlphaCharacters(value)
                ? formatMessage(messages.FirstNameInputValidationMessage)
                : undefined
            }
          />
        )}
        {fields.lastName && (
          <FormField
            {...formHelper.field(fields.lastName)}
            validate={(value) =>
              !value || !validateAlphaCharacters(value)
                ? formatMessage(messages.LastNameInputValidationMessage)
                : undefined
            }
          />
        )}
        {fields.middleName && <FormField {...formHelper.field(fields.middleName)} />}
        {fields.preferredName && <FormField {...formHelper.field(fields.preferredName)} />}
        {fields.phone && (
          <FormField
            {...formHelper.field(fields.phone)}
            className={style.PhoneInput}
            validate={validateRequiredPhone}
          />
        )}
        {fields.phoneType && (
          <FormField
            {...formHelper.field(fields.phoneType)}
            errorMessage={formatMessage(messages.PhoneInputValidationMessage)}
          />
        )}
        {fields.email && (
          <FormField {...formHelper.field(fields.email)} validate={validateEmailAddress} />
        )}
        {fields.address && <FormField {...formHelper.field(fields.address)} />}
        {!optionalResumeValidation && fields.profileLinks && (
          <FormField
            {...formHelper.field(fields.profileLinks)}
            profileLinks={formHelper.field(fields.profileLinks).schema.properties.profileLinks}
            types={formHelper.field(fields.profileLinks).schema.properties.profileLinks['ui:types']}
            validate={optionalResumeValidation ? validateOptionalResume : undefined}
          />
        )}
      </div>

      <FormFieldset className={formStyle.FormSection}>
        {optionalResumeValidation && (
          <p
            className={style.OptionalFieldsMessage}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: formatMessage(messages.OptionalResumeMessage) }}
          />
        )}
        {optionalResumeValidation && fields.profileLinks && (
          <FormField
            {...formHelper.field(fields.profileLinks)}
            initialValue={[{ type: '', link: '' }]}
            max="1"
            types={formHelper.field(fields.profileLinks).schema.properties.profileLinks['ui:types']}
            validate={optionalResumeValidation && validateOptionalResume}
          />
        )}
        {fields.evidenceOfExcellence && (
          <FormField
            {...formHelper.field(fields.evidenceOfExcellence)}
            validate={optionalResumeValidation && validateOptionalResume}
          />
        )}
        {fields.resume && (
          <div className={`${formStyle.FormFieldHalfWidth}`}>
            <FormField
              {...formHelper.field(fields.resume)}
              validate={optionalResumeValidation && validateOptionalResume}
            />
          </div>
        )}
      </FormFieldset>

      {fields.other && (
        <FormFieldset className={formStyle.FormSection}>
          {fields.other.map((field) => (
            <div className={`${formStyle.FormFieldHalfWidth}`}>
              <FormField {...formHelper.field(field)} />
            </div>
          ))}
        </FormFieldset>
      )}
    </>
  );
};

PersonalSection.propTypes = {
  optionalResumeValidation: bool,
  fields: shape({
    firstName: string,
    lastName: string,
    middleName: string,
    preferredName: string,
    phoneType: string,
    phone: string,
    email: string,
    address: string,
    resume: string,
    profileLinks: string,
    evidenceOfExcellence: string,
    other: arrayOf(string),
  }),
};

PersonalSection.defaultProps = {
  optionalResumeValidation: false,
  fields: {
    firstName: '',
    lastName: '',
    middleName: '',
    preferredName: '',
    phoneType: '',
    phone: '',
    email: '',
    address: '',
    resume: '',
    profileLinks: '',
    evidenceOfExcellence: '',
    other: [],
  },
};

export default PersonalSection;
